import { useState } from "react";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { Clock } from "lucide-react";
import { logEdgeFunctionError } from "@/utils/sentry";
import { PRODUCT_TO_TIER } from "@/constants/stripeTiers";
import { cancelledMidTrial } from "@/lib/subscription";
import TrialSkipDialog from "@/components/TrialSkipDialog";

const STARTER_UNLOCK_COPY =
  "Po aktywacji odblokujesz: 3 inspiracje, 3 konkurentów, 5 generacji dopasowanych i 15 podstawowych.";
const PRO_UNLOCK_COPY =
  "Po aktywacji odblokujesz: 6 inspiracji, 6 konkurentów, 30 generacji dopasowanych i 60 podstawowych.";

const TrialBanner = () => {
  const { isTrial, trialEnd, productId, cancelAtPeriodEnd, subscriptionTrialEnd } =
    useSubscription();
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);

  if (!isTrial || !trialEnd) return null;

  const now = new Date();
  const end = new Date(trialEnd);
  const diffMs = end.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

  const isCancelled = cancelledMidTrial({
    subscriptionTrialEnd: subscriptionTrialEnd ?? trialEnd,
    cancelAtPeriodEnd,
  }, now);

  const planTier = productId ? PRODUCT_TO_TIER[productId] : null;
  const unlockCopy = planTier === "pro" ? PRO_UNLOCK_COPY : STARTER_UNLOCK_COPY;

  const handleManage = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      logEdgeFunctionError("customer-portal", err);
    }
  };

  return (
    <div
      className="px-3 py-2 flex flex-col sm:flex-row items-center justify-center gap-x-3 gap-y-1 text-sm"
      style={{
        background:
          "linear-gradient(135deg, rgba(255, 237, 213, 0.7) 0%, rgba(254, 215, 170, 0.7) 100%)",
        backdropFilter: "blur(20px) saturate(150%)",
        WebkitBackdropFilter: "blur(20px) saturate(150%)",
        borderBottom: "1px solid rgba(251, 146, 60, 0.25)",
        boxShadow: "0 1px 12px -2px rgba(251, 146, 60, 0.15)",
      }}
    >
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-orange-500 flex-shrink-0" />
        <span className="text-orange-900/90">
          Okres próbny{" "}
          {daysLeft > 0 ? (
            <>
              — zostało{" "}
              <strong className="font-semibold">
                {daysLeft} {daysLeft === 1 ? "dzień" : daysLeft < 5 ? "dni" : "dni"}
              </strong>
            </>
          ) : (
            <>— kończy się dzisiaj</>
          )}
        </span>
      </div>

      {isCancelled ? (
        <div className="flex items-center gap-2">
          <span className="text-orange-900/90">
            Anulowałeś subskrypcję. Dostęp wygasa za {daysLeft} {daysLeft === 1 ? "dzień" : "dni"}.
            Wznów subskrypcję, aby zatrzymać dane.
          </span>
          <button
            onClick={handleManage}
            className="rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-xs font-semibold px-4 py-1.5 transition-all shadow-sm hover:shadow-md"
          >
            Wznów
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <span className="text-orange-900/80">{unlockCopy}</span>
          <button
            onClick={() => setSkipDialogOpen(true)}
            className="rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-xs font-semibold px-4 py-1.5 transition-all shadow-sm hover:shadow-md"
          >
            Aktywuj teraz
          </button>
        </div>
      )}
      <TrialSkipDialog open={skipDialogOpen} onOpenChange={setSkipDialogOpen} />
    </div>
  );
};

export default TrialBanner;
